import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Tv as TvIcon, Building2 } from 'lucide-react';
import { apiClient } from '@/services/api';
import { cn } from '@/utils/cn';

/**
 * Timeline diária de uso das TVs.
 *
 * Cada linha = 1 dia. Cada barra = 1 sessão (período contínuo entre heartbeats
 * com gap <= 90s). Eixo horizontal = 00h-24h (timezone local do navegador).
 *
 * Filtros: TV (single-select) e período (7d/30d/90d).
 */

interface Session {
  id: number;
  tv_id: number;
  started_at: string;
  ended_at: string;
  duration_s: number;
}

interface Tv {
  id: number;
  name: string;
  location?: string | null;
  store_id?: number | null;
}

interface Store {
  id: number;
  name: string;
  short_name?: string | null;
  is_default?: boolean;
}

interface SessionsResponse {
  from: string;
  to: string;
  sessions: Session[];
  tvs: Tv[];
  stores: Store[];
}

type PeriodOpt = 7 | 30 | 90;

export function TvSessionsPage() {
  const [period, setPeriod] = useState<PeriodOpt>(7);
  const [tvFilter, setTvFilter] = useState<string>('all');
  const [storeFilter, setStoreFilter] = useState<string>('all');

  // Calcula janela em ISO (UTC). Quando troca filtro, react-query refaz fetch.
  const { fromIso, toIso } = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - period);
    return { fromIso: from.toISOString(), toIso: to.toISOString() };
  }, [period]);

  const { data, isLoading } = useQuery<SessionsResponse>({
    queryKey: ['tv-sessions', tvFilter, storeFilter, period],
    queryFn: () => apiClient.getTvSessions({
      tv_id: tvFilter === 'all' ? undefined : Number(tvFilter),
      store_id: storeFilter === 'all' ? undefined : Number(storeFilter),
      from: fromIso,
      to: toIso,
    }).then(r => r.data),
  });

  const sessions = data?.sessions ?? [];
  const allTvs = data?.tvs ?? [];
  const stores = data?.stores ?? [];
  const showStoreFilter = stores.length > 1;

  // Quando uma loja é selecionada, restringe o dropdown de TVs ao subconjunto.
  const tvs = useMemo(() => {
    if (storeFilter === 'all') return allTvs;
    const sid = Number(storeFilter);
    return allTvs.filter(t => t.store_id === sid);
  }, [allTvs, storeFilter]);

  const tvById = useMemo(() => Object.fromEntries(allTvs.map(t => [t.id, t])), [allTvs]);
  const storeById = useMemo(() => Object.fromEntries(stores.map(s => [s.id, s])), [stores]);

  // Resumo de horas por loja (no período corrente)
  const hoursByStore = useMemo(() => {
    const map = new Map<number | string, number>();
    for (const s of sessions) {
      const tv = tvById[s.tv_id];
      const key = tv?.store_id ?? 'none';
      map.set(key, (map.get(key) ?? 0) + s.duration_s);
    }
    return map;
  }, [sessions, tvById]);

  // Agrupa sessões por (dia, storeKey). storeKey = store_id da TV ou 'none'.
  // Cada sessão pode cruzar meia-noite — quando isso acontece, separamos em
  // dois pedaços, um pra cada dia, pra timeline ficar correta visualmente.
  type Segment = { tvId: number; storeKey: number | 'none'; startMin: number; endMin: number; durS: number; sessId: number };
  const sessionsByDay = useMemo(() => {
    const map = new Map<string, Segment[]>();
    for (const s of sessions) {
      const tv = tvById[s.tv_id];
      const storeKey: number | 'none' = tv?.store_id ?? 'none';
      const start = new Date(s.started_at);
      const end = new Date(s.ended_at);
      let cursor = new Date(start);
      while (cursor < end) {
        const dayKey = ymd(cursor);
        const dayEnd = endOfDay(cursor);
        const sliceEnd = end < dayEnd ? end : dayEnd;
        const startMin = cursor.getHours() * 60 + cursor.getMinutes() + cursor.getSeconds() / 60;
        const endMin = sliceEnd.getHours() * 60 + sliceEnd.getMinutes() + sliceEnd.getSeconds() / 60;
        const adjustedEnd = sliceEnd >= dayEnd ? 24 * 60 : endMin;
        const arr = map.get(dayKey) ?? [];
        arr.push({
          tvId: s.tv_id,
          storeKey,
          startMin,
          endMin: adjustedEnd,
          durS: s.duration_s,
          sessId: s.id,
        });
        map.set(dayKey, arr);
        cursor = new Date(dayEnd.getTime() + 1);
      }
    }
    return map;
  }, [sessions, tvById]);

  // Quando agrupando por loja: lista ordenada de storeKeys que têm sessões na janela
  const activeStoreKeys = useMemo(() => {
    const keys = new Set<number | 'none'>();
    for (const segs of sessionsByDay.values()) for (const s of segs) keys.add(s.storeKey);
    const arr = [...keys];
    // ordena: default primeiro, depois nome, "none" no fim
    arr.sort((a, b) => {
      if (a === 'none') return 1;
      if (b === 'none') return -1;
      const sa = storeById[a as number];
      const sb = storeById[b as number];
      if (sa?.is_default && !sb?.is_default) return -1;
      if (sb?.is_default && !sa?.is_default) return 1;
      return (sa?.name ?? '').localeCompare(sb?.name ?? '');
    });
    return arr;
  }, [sessionsByDay, storeById]);

  // Quando agrupa: mostra um header por loja entre as linhas. Ativo só quando
  // o usuário não filtrou já por loja e existe mais de uma loja com dados.
  const groupByStore = showStoreFilter && storeFilter === 'all' && activeStoreKeys.length > 1;

  // Lista de dias na janela (DESC = mais recente em cima), inclusive sem dados.
  const days = useMemo(() => {
    const out: string[] = [];
    if (!data) return out;
    const from = new Date(data.from);
    const to = new Date(data.to);
    const cur = new Date(to);
    cur.setHours(0, 0, 0, 0);
    const stopAt = new Date(from);
    stopAt.setHours(0, 0, 0, 0);
    while (cur >= stopAt) {
      out.push(ymd(cur));
      cur.setDate(cur.getDate() - 1);
    }
    return out;
  }, [data]);

  // Paleta de cores por TV — deterministic via index. Quando filtra 1 TV, todas
  // as barras ficam da cor da brand pra evitar confusão.
  const tvColor = (tvId: number) => {
    if (tvFilter !== 'all') return COLORS[0];
    const idx = tvs.findIndex(t => t.id === tvId);
    return COLORS[(idx < 0 ? 0 : idx) % COLORS.length];
  };

  // Total agregado pra header (resumo do filtro atual).
  const totalSeconds = sessions.reduce((s, x) => s + x.duration_s, 0);

  return (
    <div className="container-app py-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Calendar className="h-7 w-7" /> Calendário de uso
          </h1>
          <p className="text-muted-foreground">
            Quando suas TVs ligaram e desligaram, derivado do heartbeat.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {showStoreFilter && (
            <select
              value={storeFilter}
              onChange={e => {
                setStoreFilter(e.target.value);
                // Se a TV selecionada não pertence à loja escolhida, reseta pra "todas".
                if (e.target.value !== 'all' && tvFilter !== 'all') {
                  const sid = Number(e.target.value);
                  const tvInStore = allTvs.some(t => t.id === Number(tvFilter) && t.store_id === sid);
                  if (!tvInStore) setTvFilter('all');
                }
              }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="all">Todas as lojas</option>
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' • default' : ''}</option>
              ))}
            </select>
          )}
          <select
            value={tvFilter}
            onChange={e => setTvFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">Todas as TVs</option>
            {tvs.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <div className="inline-flex rounded-md border border-input p-1 bg-background">
            {([7, 30, 90] as PeriodOpt[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'px-3 py-1.5 rounded text-sm font-medium transition',
                  period === p ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="flex gap-6 text-sm flex-wrap">
        <div>
          <span className="text-muted-foreground">Sessões:</span>{' '}
          <span className="font-bold">{sessions.length}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Tempo total ligado:</span>{' '}
          <span className="font-bold">{formatDuration(totalSeconds)}</span>
        </div>
        {showStoreFilter && storeFilter === 'all' && hoursByStore.size > 1 && (
          <div className="flex gap-3 flex-wrap items-center">
            <span className="text-muted-foreground">Por loja:</span>
            {stores.map(s => {
              const secs = hoursByStore.get(s.id) ?? 0;
              if (secs <= 0) return null;
              return (
                <span key={s.id} className="inline-flex items-center gap-1 rounded bg-muted/60 px-2 py-0.5 text-xs">
                  <Building2 className="h-3 w-3" />
                  <span className="font-medium">{s.short_name || s.name}</span>
                  <span className="text-muted-foreground">{formatDuration(secs)}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Legenda — só quando exibindo todas as TVs */}
      {tvFilter === 'all' && tvs.length > 0 && (
        <div className="flex gap-3 flex-wrap text-xs">
          {tvs.map((t, i) => (
            <div key={t.id} className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span>{t.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-xl border bg-card overflow-hidden">
        {/* Header de horas */}
        <div className="grid grid-cols-[120px_1fr] border-b bg-muted/30 text-xs text-muted-foreground">
          <div className="px-3 py-2 font-semibold">Dia</div>
          <div className="relative h-8">
            {HOURS_LABELS.map(h => (
              <div
                key={h}
                className="absolute top-0 bottom-0 border-l border-border/40 px-1 pt-2"
                style={{ left: `${(h / 24) * 100}%` }}
              >
                {h.toString().padStart(2, '0')}h
              </div>
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="p-12 text-center text-muted-foreground text-sm">Carregando…</div>
        )}

        {!isLoading && sessions.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">
            <TvIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-semibold">Sem dados pra esse período</p>
            <p className="text-sm mt-1">
              A coleta começou recentemente — após algumas horas online, as sessões aparecem aqui.
            </p>
          </div>
        )}

        {!isLoading && sessions.length > 0 && !groupByStore && days.map(day => {
          const daySessions = sessionsByDay.get(day) ?? [];
          return (
            <DayRow
              key={day}
              day={day}
              segments={daySessions}
              tvColor={tvColor}
              tvById={tvById}
              storeById={storeById}
              showStoreInTooltip={showStoreFilter && storeFilter === 'all'}
            />
          );
        })}

        {!isLoading && sessions.length > 0 && groupByStore && activeStoreKeys.map(storeKey => {
          const storeLabelText = storeKey === 'none'
            ? 'Sem loja'
            : (storeById[storeKey]?.name ?? `Loja #${storeKey}`);
          return (
            <div key={String(storeKey)} className="border-b last:border-b-0">
              <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {storeLabelText}
              </div>
              {days.map(day => {
                const daySessions = (sessionsByDay.get(day) ?? []).filter(seg => seg.storeKey === storeKey);
                if (daySessions.length === 0) return null;
                return (
                  <DayRow
                    key={`${storeKey}-${day}`}
                    day={day}
                    segments={daySessions}
                    tvColor={tvColor}
                    tvById={tvById}
                    storeById={storeById}
                    showStoreInTooltip={false}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponente
// ---------------------------------------------------------------------------

interface DayRowProps {
  day: string;
  segments: Array<{ tvId: number; storeKey: number | 'none'; startMin: number; endMin: number; durS: number; sessId: number }>;
  tvColor: (tvId: number) => string;
  tvById: Record<number, Tv>;
  storeById: Record<number, Store>;
  showStoreInTooltip: boolean;
}
function DayRow({ day, segments, tvColor, tvById, storeById, showStoreInTooltip }: DayRowProps) {
  return (
    <div className="grid grid-cols-[120px_1fr] border-b last:border-b-0 hover:bg-muted/10">
      <div className="px-3 py-3 text-sm font-medium border-r border-border/60">
        {formatDay(day)}
      </div>
      <div className="relative h-10">
        {[0, 6, 12, 18, 24].map(h => (
          <div
            key={h}
            className="absolute top-0 bottom-0 border-l border-border/30"
            style={{ left: `${(h / 24) * 100}%` }}
          />
        ))}
        {segments.map((seg, i) => {
          const widthPct = ((seg.endMin - seg.startMin) / (24 * 60)) * 100;
          const leftPct = (seg.startMin / (24 * 60)) * 100;
          const w = Math.max(widthPct, 0.3);
          const tvName = tvById[seg.tvId]?.name ?? `TV ${seg.tvId}`;
          const storeLabel = showStoreInTooltip && typeof seg.storeKey === 'number'
            ? ` · ${storeById[seg.storeKey]?.name ?? ''}`
            : '';
          return (
            <div
              key={`${seg.sessId}-${i}`}
              className="absolute top-1.5 bottom-1.5 rounded-sm transition-opacity hover:opacity-80"
              style={{
                left: `${leftPct}%`,
                width: `${w}%`,
                backgroundColor: tvColor(seg.tvId),
              }}
              title={`${tvName}${storeLabel} · ${formatTimeRange(seg.startMin, seg.endMin)} · ${formatDuration(seg.durS)}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HOURS_LABELS = [0, 3, 6, 9, 12, 15, 18, 21];

// Paleta acessível — cores distintas, contraste OK em fundo branco/card.
const COLORS = [
  '#7c3aed', // brand-600 (roxo)
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#6366f1', // indigo-500
];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function formatDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (date.getTime() === today.getTime()) return 'Hoje';
  if (date.getTime() === yesterday.getTime()) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', weekday: 'short' });
}
function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}min`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
function formatTimeRange(startMin: number, endMin: number): string {
  return `${hhmm(startMin)}–${hhmm(endMin)}`;
}
function hhmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return `${pad(h)}:${pad(m)}`;
}
